<template>
  <div
    class="assetgroup"
    :class="classObject"
    @dragenter="dragEnter = true"
    @dragleave="dragEnter = false"
    @dragover.prevent
    @mousedown="onMousedownAssetgroup"
  >
    <vddl-list
      class="panel__body--list"
      :list="assets"
      :horizontal="true"
      :drop="onDropped"
      :allowed-types="allowedTypes"
      :disable-if="!canEdit"
    >
      <asset-collection-item
        v-for="(asset, index) in assets"
        :id="id"
        :key="asset.id"
        :list="assets"
        :item="assetNodes[index]"
        :draggable="asset"
        :index="index"
        :wrapper="assets"
        :type="type"
        :disable-if="!canEdit"
        :move-action="moveAction"
        :delete-action="deleteAction"
      />
      <vddl-placeholder>
        <div class="droparea" />
      </vddl-placeholder>
    </vddl-list>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, DRAG_TYPE } from '@/constants.js';
import AssetCollectionItem from './AssetCollectionItem.vue';

export default {
  components: {
    AssetCollectionItem,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
    // The asset references that the list contains, format: { id, asset }
    assets: {
      type: Array,
      default: null,
    },
    // The type of the dragged item for this list e.g. DRAG_TYPE.ELEMENT_COMPONENT
    type: {
      type: String,
      default: null,
    },
    // The type of this list e.g. DRAG_LIST.ELEMENTS
    listType: {
      type: String,
      default: null,
    },
    // What are the types of items that are allowed to drag on this list
    allowedTypes: {
      type: Array,
      default: null,
    },
    // A callback when an item is moved from this list
    moveAction: {
      type: Function,
      default: () => {},
    },
    // A callback when an item is removed from this list
    deleteAction: {
      type: Function,
      default: () => {},
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragEnter: false,
    };
  },

  computed: {
    ...mapGetters('application/drag', ['getItems']),
    ...mapGetters('project/data/assets', ['getNodes']),

    /**
     * Returns an array with the actual asset objects
     * @returns {Array}
     */
    assetNodes() {
      const ids = this.assets.map(c => c.asset);
      return this.getNodes(ids);
    },

    /**
     * Return whether there is an asset currently dragged over the asset collection
     * @returns {Boolean}
     */
    isAssetDraggedOver() {
      const { type } = this.getItems;
      return (
        this.dragEnter &&
        (type === DRAG_TYPE.ASSET ||
          type === DRAG_TYPE.ATTRIBUTE_ASSET ||
          type === DRAG_TYPE.SEARCH_ASSET)
      );
    },

    classObject() {
      return {
        'asset-drag-over': this.isAssetDraggedOver,
      };
    },
  },

  methods: {
    ...mapActions('application/drag', ['setTarget']),

    /**
     * Event handler for vddl drop event on a list. This is triggered before the move vddl event on the item itself.
     */
    onDropped(event) {
      this.setTarget({
        value: {
          type: this.listType,
          id: this.id,
          index: event.index,
        },
      });
    },

    onMousedownAssetgroup(event) {
      this.$emit('onMousedownAssetgroup', event);
    },
  },
};
</script>
<style scoped lang="scss">
.attribute {
  .assetgroup {
    margin: 0;
    padding: 0;

    .vddl-draggable {
    }
    &.asset-drag-over {
      &.is-empty {
        .vddl-list {
          border-color: #db841e;
          &:after {
            color: #db841e;
          }
        }
      }
    }
    &.is-empty {
      // position: relative;
      .vddl-list {
        margin: 0;
        border: 2px dashed #535f61;
        border-radius: 2px;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 56px;
        .vddl-placeholder {
          display: none;
        }
        &:after {
          content: 'Drag assets to add';
          text-align: center;
          text-transform: uppercase;
          color: #6c7b7d;
        }
      }
    }
    .vddl-list {
      border: 2px transparent;
      padding: 0;
      display: flex;
      flex: 1;
      // min-height: 66px;
      width: 100%;
      background: #2c3233;
      overflow: visible;
      column-gap: 10px;
      row-gap: 10px;
      .vddl-placeholder {
        pointer-events: none;
      }
    }
  }
}

.assetgroup {
  margin: 0 20px 0 20px;
  padding: 16px 0 0 0;
  cursor: default;
  position: relative;
}
.assetgroup .vddl-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  overflow: hidden;
}

.dragover {
  .assetgroup .vddl-list {
    min-height: 70px;

    &.asset-drag-over .placeholder {
      display: none;
    }

    &.asset-drag-over * {
      pointer-events: none;
    }
  }

  .assetgroup .placeholder {
    height: 64px;
    width: 64px;
    border-radius: 4px;
    overflow: hidden;
    margin: 3px 4px;
    position: relative;
    border: 2px dashed #db841e;
  }
}
</style>

<style lang="scss">
.assetgroup .vddl-placeholder {
  position: relative;
  height: 36px;
  width: 72px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px dashed #db841e;
}

.assetgroup .vddl-draggable,
.assetgroup .vddl-list {
  position: relative;
}

.assetgroup .vddl-dragging-source {
  display: none;
}
</style>
